import { network } from '@utils'
import {
  ActionFunction,
  handleReturn,
  MutationFunction,
  PaginatedResponse,
  QueryFunction
} from './root'

export interface IVatPeriod {
  id: number
  period: number
  start_date: string
  end_date: string
  due_date: string
  is_locked: boolean
  is_reported: boolean
  is_recapitulative_reported: boolean
  has_eu_sales: boolean
  has_locked_accounting_period: boolean
  has_due_passed: boolean
  has_ended: boolean
  recapitulative_due_date?: string | null
  filing_tax_to_be_paid?: number | null
  filing_accepted_at?: string | null
  purchase_invoice_id?: number | null
}

export interface ElsterVatReportResponse {
  tax_to_be_paid?: number | null
  purchase_invoice_id?: number | null
}

interface VatPeriodListProps {
  slug: string
}

export const fetchVatPeriods: QueryFunction<
  VatPeriodListProps,
  PaginatedResponse<IVatPeriod>
> = async ({ slug }, params = null) => {
  const url = `/v1/business/${slug}/vat_period/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

interface VatPeriodInstanceProps {
  slug: string
  vatPeriodId: number
}

export const fetchVatPeriod: QueryFunction<VatPeriodInstanceProps, IVatPeriod> = async (
  { slug, vatPeriodId },
  params = null
) => {
  const url = `/v1/business/${slug}/vat_period/${vatPeriodId}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export const createVatPeriod: MutationFunction<VatPeriodListProps, IVatPeriod> = async (
  { slug },
  requestData
) => {
  const url = `/v1/business/${slug}/vat_period/`
  const { data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: requestData
  })
  return data
}

export const updateVatPeriod: MutationFunction<VatPeriodInstanceProps, IVatPeriod> = async (
  { slug, vatPeriodId },
  requestData
) => {
  const url = `/v1/business/${slug}/vat_period/${vatPeriodId}/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'PATCH',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const deleteVatPeriod: MutationFunction<VatPeriodInstanceProps, void> = async ({
  slug,
  vatPeriodId
}) => {
  const url = `/v1/business/${slug}/vat_period/${vatPeriodId}/`
  await network.httpClient.request({
    url,
    method: 'DELETE'
  })
}

export const lockVatPeriod: MutationFunction<VatPeriodInstanceProps, void> = async ({
  slug,
  vatPeriodId
}) => {
  const url = `/v1/business/${slug}/vat_period/${vatPeriodId}/action/lock/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const unlockVatPeriod: MutationFunction<VatPeriodInstanceProps, void> = async ({
  slug,
  vatPeriodId
}) => {
  const url = `/v1/business/${slug}/vat_period/${vatPeriodId}/action/unlock/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const reportVatPeriod: MutationFunction<VatPeriodInstanceProps, void> = async ({
  slug,
  vatPeriodId
}) => {
  const url = `/v1/business/${slug}/vat_period/${vatPeriodId}/action/report/`
  await network.httpClient.request({
    url,
    method: 'POST'
  })
}

export const reportVatPeriodRecapitulative: MutationFunction<
  VatPeriodInstanceProps,
  void
> = async ({ slug, vatPeriodId }) => {
  const url = `/v1/business/${slug}/vat_period/${vatPeriodId}/action/report_recapitulative/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export interface IVatReporterContactDetails {
  verohallinto_latest_reporter_full_name: string
  verohallinto_latest_reporter_phone_number: string
}

export const reportVatPeriodToVerohallinto: ActionFunction<
  VatPeriodInstanceProps,
  IVatReporterContactDetails,
  IVatPeriod
> = async ({ slug, vatPeriodId }, reporterDetails) => {
  const url = `/v1/business/${slug}/vat_period/${vatPeriodId}/action/report_to_verohallinto/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: {
      verohallinto_latest_reporter_full_name:
        reporterDetails.verohallinto_latest_reporter_full_name,
      verohallinto_latest_reporter_phone_number:
        reporterDetails.verohallinto_latest_reporter_phone_number
    },
    timeout: 120000,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const reportVatPeriodToElster: MutationFunction<
  VatPeriodInstanceProps,
  ElsterVatReportResponse
> = async ({ slug, vatPeriodId }) => {
  const url = `/v1/business/${slug}/vat_period/${vatPeriodId}/action/report_to_elster/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    timeout: 180000,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

interface VatPeriodAuthorizationProps {
  slug: string
}

interface Message {
  header: string
  body: string
}
export interface VatPeriodAuthorizationResponse {
  ok: boolean
  message: Message
  link?: string
}

export const checkVatReportingAuthorization: QueryFunction<
  VatPeriodAuthorizationProps,
  VatPeriodAuthorizationResponse
> = async ({ slug }) => {
  const url = `/v1/business/${slug}/vat_period/has_suomi_authentication/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}
